import { ChangeEvent, DragEvent, useRef, useState, useEffect, useMemo } from "react";
import { Check, X, CloudUpload, ExternalLink, Trash2 } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { Button } from "../Button";
import { Loader } from "../Loader";
import { Label } from "../Label";
import { Type } from "../Type";
import { Input } from "../Input";
import { FieldError } from "../FieldError";
import {
  dropzoneBaseStyles,
  dropzoneDragActiveStyles,
  dropzoneIconStyles,
  dropzoneHintStyles,
} from "../Dropzone";
import { FileType, AllowedFileTypes } from "@core/types/file.types";

// Architecture Note: This FileUploader intentionally does NOT wrap FileUploaderBase.
// Reason: This component has complex business logic (progress tracking, multiple file
// state management, file list rendering with preview links) that goes beyond the
// simple file selection behavior provided by FileUploaderBase.
// FileUploaderBase is designed for simple file selection with drag-and-drop,
// while this component handles the full upload lifecycle with progress.

export type Props = {
  /** Unique hash for the file input ID */
  hash?: string;
  /** Message to display in the dropzone */
  dropzoneMessage?: string;
  /** Text describing accepted file types */
  acceptedFileTypes?: string;
  /** Map of allowed MIME types */
  allowedFileTypes?: AllowedFileTypes;
  /** Additional CSS classes */
  className?: string;
  /** Handler for uploading files with progress callback */
  uploadFileHandler: (
    file: File,
    onProgress: (progress: number) => void
  ) => Promise<Partial<FileType>>;
  /** Callback when uploaded files change */
  onUploadedFilesChange?: (files: FileType[]) => void;
  /** Callback when all files have been processed */
  onAllFilesHandled?: () => void;
  /** Callback when alt text is updated for a file */
  onAltTextChange?: (mediaId: number, altText: string) => void;
  /** Initial files to display (previously uploaded) */
  initialFiles?: FileType[];
  /** Maximum number of files allowed */
  maxFiles?: number;
  /** Maximum file size in bytes */
  maxSize?: number;
  /** Callback when upload state changes (includes file presence and alt text validity) */
  onUploadStateChange?: (state: { hasFiles: boolean; isAltTextValid: boolean }) => void;
  /** Show remove button for completed files */
  showRemoveButton?: boolean;
  /** Callback when a file is removed */
  onFileRemove?: (fileId: number) => void;
  /** Test ID for testing */
  "data-testid"?: string;
};

const ALLOWED_FILE_TYPES: AllowedFileTypes = {
  "image/jpeg": true,
  "image/png": true,
  "image/gif": true,
  "image/svg+xml": true,
};

const MIME_TO_EXTENSIONS: Record<string, string> = {
  "image/jpeg": ".jpg,.jpeg",
  "image/png": ".png",
  "image/gif": ".gif",
  "image/svg+xml": ".svg",
  "image/webp": ".webp",
  "application/pdf": ".pdf",
  "application/msword": ".doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    ".docx",
  "application/vnd.ms-excel": ".xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "text/plain": ".txt",
  "audio/mpeg": ".mp3",
  "audio/wav": ".wav",
  "audio/ogg": ".ogg",
  "audio/webm": ".webm",
  "audio/aac": ".aac",
  "audio/mp4": ".m4a",
  "audio/x-m4a": ".m4a",
};

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

const isImageFile = (mimeType?: string) => mimeType?.startsWith("image/");

/**
 * Styled file uploader component with progress tracking and file list.
 * Supports drag and drop, multiple files, and upload progress.
 *
 * For a simpler file uploader without progress tracking, use FileUploaderBase from @core/ui.
 */
export function FileUploader({
  hash = "",
  className,
  uploadFileHandler,
  onAllFilesHandled,
  onUploadedFilesChange,
  onAltTextChange,
  dropzoneMessage = "Click to upload or drag and drop",
  acceptedFileTypes = "Images (JPG, PNG, GIF, SVG)",
  allowedFileTypes = ALLOWED_FILE_TYPES,
  initialFiles = [],
  maxFiles,
  maxSize,
  onUploadStateChange,
  showRemoveButton = false,
  onFileRemove,
  "data-testid": testId = "file-uploader",
}: Props) {
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<FileType[]>(initialFiles);

  // Check if max files limit is reached
  const isMaxFilesReached = maxFiles !== undefined && uploadedFiles.length >= maxFiles;

  // Derive accept attribute from allowedFileTypes
  const acceptAttribute = useMemo(() => {
    const extensions = Object.keys(allowedFileTypes)
      .map((mime) => MIME_TO_EXTENSIONS[mime])
      .filter(Boolean);
    return extensions.length > 0 ? extensions.join(",") : undefined;
  }, [allowedFileTypes]);

  const inFlightUploads = useRef(0);

  // Track upload state (file presence and alt text validation)
  useEffect(() => {
    if (!onUploadStateChange) return;

    const completedImages = uploadedFiles.filter(
      (f) => f.progress === 100 && !f.error && isImageFile(f.mimeType)
    );

    const hasFiles = completedImages.length > 0;

    // Valid if there are no completed images, or all have non-empty alt text
    const isAltTextValid =
      completedImages.length === 0 ||
      completedImages.every((f) => f.altText && f.altText.trim() !== "");

    onUploadStateChange({ hasFiles, isAltTextValid });
  }, [uploadedFiles, onUploadStateChange]);

  const checkIfAllHandled = () => {
    if (inFlightUploads.current === 0) {
      onAllFilesHandled?.();
    }
  };

  const latestFiles = useRef(initialFiles);

  const updateFiles = (updater: (prev: FileType[]) => FileType[]) => {
    setUploadedFiles((prev) => {
      const next = updater(prev);
      latestFiles.current = next;
      return next;
    });
  };

  // Notify parent of file changes after render, not during state update
  useEffect(() => {
    onUploadedFilesChange?.(uploadedFiles);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles]);
  const validateFile = (file: File): boolean => {
    if (isMaxFilesReached) {
      setValidationError(`Maximum of ${maxFiles} file(s) allowed.`);
      return false;
    }
    if (!allowedFileTypes[file.type]) {
      setValidationError(`File type ${file.type} is not supported.`);
      return false;
    }
    if (maxSize && file.size > maxSize) {
      setValidationError(
        `File size exceeds maximum allowed size of ${formatBytes(maxSize)}`
      );
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleUpload = async (file: File) => {
    const fileId = Math.random();

    const placeholder: FileType = {
      id: fileId,
      name: file.name,
      size: file.size.toString(),
      progress: 0,
      error: false,
      checked: false,
      mimeType: file.type,
    };

    inFlightUploads.current += 1;

    updateFiles((prev) => [...prev, placeholder]);

    try {
      const mediaObject = await uploadFileHandler(file, (progress: number) => {
        updateFiles((prev) =>
          prev.map((f) => (f.id === fileId ? { ...f, progress } : f))
        );
      });

      updateFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? { ...f, ...mediaObject, progress: 100, mimeType: file.type }
            : f
        )
      );
    } catch (err) {
      updateFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, error: true, progress: 0 } : f
        )
      );
    } finally {
      inFlightUploads.current -= 1;
      checkIfAllHandled();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      Array.from(files).forEach((file) => {
        if (validateFile(file)) {
          handleUpload(file);
        }
      });
    }
  };

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();

    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const files = event.dataTransfer.files;
    if (files) {
      Array.from(files).forEach((file) => {
        if (validateFile(file)) {
          handleUpload(file);
        }
      });
    }
  };

  const handleAltTextChange = (file: FileType, altText: string) => {
    // Update local state and clear error if user starts typing
    updateFiles((prev) =>
      prev.map((f) =>
        f.id === file.id
          ? { ...f, altText, altTextError: altText.trim() === "" }
          : f
      )
    );
  };

  const handleAltTextBlur = (file: FileType) => {
    // Mark as touched and validate
    const isEmpty = !file.altText || file.altText.trim() === "";
    updateFiles((prev) =>
      prev.map((f) =>
        f.id === file.id ? { ...f, altTextTouched: true, altTextError: isEmpty } : f
      )
    );

    // Call the callback to persist to server (only if not empty)
    if (onAltTextChange && file.id && file.altText && file.altText.trim() !== "") {
      onAltTextChange(Number(file.id), file.altText);
    }
  };

  const handleRemoveFile = (file: FileType) => {
    updateFiles((prev) => prev.filter((f) => f.id !== file.id));
    onFileRemove?.(Number(file.id));
  };

  const renderFile = (file: FileType, index: number) => (
    <div
      className={twMerge(
        "flex flex-col rounded-md border p-4 w-full gap-4",
        file.error ? "border-destructive" : "border-brand-800"
      )}
      key={index}
      data-testid={`${testId}-file-${index}`}
    >
      <div className="flex flex-col items-center justify-between gap-4 md:flex-row md:gap-0">
        <div className="flex flex-col items-center gap-2 md:flex-row">
          <a
            href={file.file}
            target="_blank"
            className="inline-flex items-center"
            data-testid={`${testId}-preview-link-${index}`}
          >
            <Button
              iconComponent={ExternalLink}
              variant="ghost"
              iconPosition="right"
            >
              {file.name}
            </Button>
          </a>
          {file.progress < 100 && (
            <Type variant="caption" className="text-brand-600" as="span">
              {`${(Number(file.size) / 1024).toFixed(2)} KB`} - {file.progress}%
              uploaded
            </Type>
          )}
          {file.error && (
            <Type variant="caption" className="text-error" as="a">
              Try again
            </Type>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          {file.error ? (
            <X
              className="h-6 w-6 text-error"
              data-testid={`${testId}-error-icon-${index}`}
            />
          ) : file.progress === 100 ? (
            <>
              <Check
                className="h-6 w-6 text-success"
                data-testid={`${testId}-success-icon-${index}`}
              />
              {showRemoveButton && (
                <Button
                  variant="ghost"
                  iconComponent={Trash2}
                  onClick={() => handleRemoveFile(file)}
                  aria-label="Remove file"
                  data-testid={`${testId}-remove-button-${index}`}
                />
              )}
            </>
          ) : (
            <Loader
              className="ml-2 h-10 w-10"
              data-testid={`${testId}-loader-${index}`}
            />
          )}
        </div>
      </div>

      {/* Alt text input for images after upload completes */}
      {file.progress === 100 && !file.error && isImageFile(file.mimeType) && (
        <div className="w-full" data-testid={`${testId}-alt-text-${index}`}>
          <Label htmlFor={`alt-text-${file.id}`} className="text-left block mb-1">
            Alt text <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`alt-text-${file.id}`}
            name={`alt-text-${file.id}`}
            placeholder="Describe this image for accessibility"
            value={file.altText || ""}
            onChange={(e) => handleAltTextChange(file, e.target.value)}
            onBlur={() => handleAltTextBlur(file)}
            error={file.altTextTouched && file.altTextError}
            data-testid={`${testId}-alt-text-input-${index}`}
          />
          {file.altTextTouched && file.altTextError && (
            <div className="text-left" data-testid={`${testId}-alt-text-error-${index}`}>
              <FieldError message="Alt text is required" />
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={twMerge(
        "flex flex-col gap-2 items-center text-center",
        className
      )}
      data-testid={testId}
    >
      {!isMaxFilesReached && (
        <div
          className={twMerge(
            dropzoneBaseStyles,
            isDragging && dropzoneDragActiveStyles
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById(`file-upload${hash}`)?.click()}
          data-testid={`${testId}-dropzone`}
        >
          <CloudUpload className={dropzoneIconStyles} />
          <input
            type="file"
            multiple={maxFiles === undefined || maxFiles > 1}
            onChange={handleFileChange}
            className="hidden"
            id={`file-upload${hash}`}
            accept={acceptAttribute}
            data-testid={`${testId}-input`}
          />
          <Label className="cursor-pointer">{dropzoneMessage}</Label>
          <Type variant="caption" className={dropzoneHintStyles}>
            {acceptedFileTypes}
          </Type>
        </div>
      )}
      {validationError && (
        <Type
          variant="caption"
          className="text-error"
          data-testid={`${testId}-error`}
        >
          {validationError}
        </Type>
      )}
      {uploadedFiles.length > 0 &&
        uploadedFiles.map((file, index) => renderFile(file, index))}
    </div>
  );
}

export default FileUploader;
